import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, ArrowUpRight } from 'lucide-react';
import { articlesApi } from '../../lib/api';

/**
 * "Editor's Picks" — a compact 3-up row showcasing the admin-pinned trio
 * (Admin → Featured Article Tab, slots 0/1/2). Auto-hides if the admin
 * hasn't pinned anything. Visually distinct from the red LatestArticleBanner
 * (uses cyan accent) so the homepage doesn't feel repetitive.
 */
export default function EditorsPicksStrip() {
  const [picks, setPicks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await articlesApi.getFeaturedList(3, true);
        if (!cancelled && Array.isArray(res.data) && res.data.length > 0) {
          setPicks(res.data);
        }
      } catch { /* silently no-op — strip just won't appear */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (picks.length === 0) return null;

  return (
    <section
      className="relative bg-black border-t border-b border-[#00F0FF]/15"
      data-testid="editors-picks-strip"
    >
      <div className="container-ninja py-8 md:py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <Bookmark className="w-4 h-4 text-[#00F0FF]" strokeWidth={2.5} />
            <span
              className="text-[#00F0FF] text-xs uppercase tracking-[0.22em] font-bold"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Editor&apos;s Picks
            </span>
            <span className="hidden sm:inline text-zinc-600 text-xs">— curated by the team</span>
          </div>
          <Link
            to="/blog"
            className="text-zinc-500 hover:text-[#00F0FF] text-xs uppercase tracking-wider transition-colors flex items-center gap-1 group"
            data-testid="editors-picks-view-all"
          >
            View all
            <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        {/* Grid: stacks vertically on mobile, horizontal trio on tablet+ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {picks.map((article, idx) => (
            <Link
              key={article.slug}
              to={`/blogs/article/${article.slug}`}
              className="group flex items-stretch gap-3 p-3 border border-zinc-800
                         hover:border-[#00F0FF]/40 bg-zinc-950/40 hover:bg-zinc-900/60
                         transition-all duration-300"
              data-testid={`editors-picks-card-${idx}`}
            >
              {/* Thumbnail */}
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 overflow-hidden bg-zinc-900">
                {article.featured_image ? (
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-zinc-700">
                    <Bookmark className="w-5 h-5" />
                  </div>
                )}
                {/* Slot number badge */}
                <span
                  className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-mono font-bold
                             bg-[#00F0FF] text-black leading-none"
                >
                  {String(idx + 1).padStart(2, '0')}
                </span>
              </div>

              {/* Text block */}
              <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                <div>
                  <p
                    className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  >
                    {article.category || 'Article'}
                  </p>
                  <h4
                    className="text-white text-sm leading-snug line-clamp-2 group-hover:text-[#00F0FF] transition-colors"
                    data-testid={`editors-picks-card-title-${idx}`}
                  >
                    {article.title}
                  </h4>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500 group-hover:text-[#00F0FF] transition-colors mt-1.5">
                  Read article
                  <ArrowUpRight className="w-2.5 h-2.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
